import cn from 'classnames';
import React from 'react';
import { getTopKCuisines } from 'src/shared/design-system/DishFirstCard/utils';
import { generateThumborImgUrl } from 'src/shared/design-system/DynamicImage';
import { ServingChip } from '.';
import { DishCardDisplayMode, MealPlanFoodItem } from '../../checkout/types';
import { UpsellPriceChip } from '../Chips/UpsellChips';
import { FoodItemRatings } from '../FoodItemRatings';
import { getDishCardImageGeometry } from './getDishCardImageGeometry';
import { MealPlanDishImage } from './MealPlanDishImage';
import { SelectedQuantityDisplay } from './SelectedQuantityDisplay';

interface IProps {
  displayMode: DishCardDisplayMode;
  foodItem: MealPlanFoodItem;
  hideServingAndPricing?: boolean;
  isDishModalEnabled?: boolean;
  onClick?: () => void;
  quantity?: number;
  displayServings: number;
  showShef: boolean;
  showNewFreshTag?: boolean;
  favoriteHeart?: React.ReactElement;

  // Cart-mode props (optional)
  remainingAvailableUnits?: number;
  quantityPicker?: React.ReactNode;
  showFlexServingsStepper?: boolean;
  servingsInCart?: number;
}

/**
 * The large meal plan dish card is used to display main dishes in the meal plan.
 * If quantityPicker is provided, the card will be in cart mode.
 */
export const MealPlanDishCardLarge: React.FC<IProps> = ({
  displayMode,
  foodItem,
  showShef,
  displayServings,
  hideServingAndPricing,
  isDishModalEnabled = true,
  onClick,
  quantity = 0,
  showNewFreshTag,
  favoriteHeart,

  quantityPicker,
  remainingAvailableUnits = Infinity,
  showFlexServingsStepper = false,
  servingsInCart = 0,
}) => {
  const isGridDisplay = displayMode === DishCardDisplayMode.Grid;
  const isCarouselDisplay = displayMode === DishCardDisplayMode.Carousel;

  const imageGeometry = getDishCardImageGeometry({ displayMode, showShef });

  const {
    id,
    imageUrl,
    ratingCount,
    averageRating,
    upsell,
    cuisineCategories,
    name,
    ingredientsFlagged,
    customIngredientsFlagged,
    merchandisingTags,
    shef,
  } = foodItem;
  const shefName = shef.firstName;

  const cuisines = getTopKCuisines({ cuisineCategories, k: 2 });

  const servingAndPriceChips = (
    <div className='flex items-center gap-2'>
      <ServingChip numServings={displayServings} />
      <UpsellPriceChip upsell={upsell} servingsPerMeal={displayServings} />
    </div>
  );

  const upsellServingsMultiplier = servingsInCart > 0 ? servingsInCart : 1;

  const shouldRenderBottomSection = !!quantityPicker || !hideServingAndPricing || quantity > 0;

  return (
    <div
      onClick={onClick}
      key={id}
      className={cn(
        `relative flex flex-col items-center justify-between`,
        'overflow-hidden rounded-md border border-neutral-100 bg-white p-0 text-left shadow-md',
        imageGeometry.cardClassName,
        {
          'mb-4 w-[308px] shrink-0 grow-0 basis-[308px] self-start': isCarouselDisplay,
          'h-full w-full': isGridDisplay,
          'cursor-pointer': !!onClick,
        }
      )}>
      <MealPlanDishImage
        className={imageGeometry.imageClassName}
        aspectRatio={imageGeometry.aspectRatio}
        zoom={imageGeometry.zoom}
        height={imageGeometry.height}
        width={imageGeometry.width}
        imageUrl={imageUrl ?? ''}
        remainingAvailableUnits={remainingAvailableUnits}
        unitsInCart={quantity}
        upsell={upsell}
        ingredientsFlagged={ingredientsFlagged}
        customIngredientsFlagged={customIngredientsFlagged}
        merchandisingTags={merchandisingTags}
        showNewFreshTag={showNewFreshTag}
        favoriteHeart={favoriteHeart}
      />
      <div className='flex size-full flex-col items-start justify-between gap-0.5 p-4'>
        {/* Shef, Dish Name, Cuisines */}
        <div>
          {showShef && (
            <p className='m-0 flex items-center gap-1 text-caption-600'>
              {shef.imageUrl && (
                <img
                  src={generateThumborImgUrl({ src: shef.imageUrl, width: 16, height: 16 })}
                  alt=''
                  className='size-4 rounded-full object-cover'
                />
              )}
              <span>Shef</span>
              <span className='line-clamp-1 font-quick-writers text-h6 text-raspberry'>{shefName}</span>
            </p>
          )}
          {cuisines && <p className='m-0 line-clamp-1 text-caption-400 text-eggplant-500'>{cuisines}</p>}
          <p
            className={cn('m-0 text-h5-bold text-eggplant', {
              'line-clamp-1': isDishModalEnabled,
              'line-clamp-2': !isDishModalEnabled,
            })}>
            {name}
          </p>
        </div>

        {/* Dish Ratings */}
        <FoodItemRatings ratingCount={ratingCount} averageRating={averageRating} foodItemId={id} />

        {/* Servings and Pricing Chips, Quantity */}
        {shouldRenderBottomSection &&
          (showFlexServingsStepper ? (
            <div className='flex h-7 w-full items-center gap-4'>
              {!hideServingAndPricing && <UpsellPriceChip upsell={upsell} servingsPerMeal={upsellServingsMultiplier} />}
              <div className='flex min-w-0 flex-1 justify-end'>
                {quantityPicker || (quantity > 0 && <SelectedQuantityDisplay quantity={quantity} />)}
              </div>
            </div>
          ) : (
            <div className='flex w-full justify-between'>
              {!hideServingAndPricing && servingAndPriceChips}
              <div className='flex justify-end'>
                {quantityPicker || (quantity > 0 && <SelectedQuantityDisplay quantity={quantity} />)}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};
